/**
 * DirectCanvas - Main canvas orchestration class.
 *
 * Phase 0.1: HTML layer, pan/zoom, coordinate transforms, frustum culling.
 * Phase 0.2: R-tree spatial index, selection, node dragging.
 *
 * This is the public API that application code uses.
 */

import type {
  Camera,
  CanvasOptions,
  ResolvedCanvasOptions,
  UINode,
  UIEdge,
  ViewportSize,
  WorldPoint,
  WorldRect,
  ScreenPoint,
} from "./types";
import * as geometry from "./geometry";
import { HTMLLayer } from "./html-layer";
import { SVGLayer } from "./svg-layer";
import { WebGLLayer, isWebGL2Available, type GridStyle } from "./webgl-layer";
import { SpatialIndex } from "./spatial-index";
import { registerBuiltinRenderers, renderersRegistered } from "./renderers";
import { rendererRegistry } from "./renderer";
import {
  DebugOverlay,
  DEBUG_OVERLAY_STYLES,
  type DebugOptions,
} from "./debug-overlay";
import { ContentMeasurer, type ContentMeasurement, type MeasureOptions } from "./content-measure";

/** Default canvas options */
const DEFAULT_OPTIONS: ResolvedCanvasOptions = {
  minZoom: 0.1,
  maxZoom: 10,
  gridSize: 24,
  showGrid: true,
  simplifyLOD: false,
};

/** Event types emitted by the canvas */
export type CanvasEventType =
  | "selectionchange"
  | "nodeclick"
  | "nodedragstart"
  | "nodedrag"
  | "nodedragend";

/** Event handler function type */
export type CanvasEventHandler<T = unknown> = (data: T) => void;

/** Selection change event data */
export interface SelectionChangeEvent {
  selected: string[];
  added: string[];
  removed: string[];
}

/** Node click event data */
export interface NodeClickEvent {
  nodeId: string;
  worldPoint: WorldPoint;
  screenPoint: ScreenPoint;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

/** Node drag event data */
export interface NodeDragEvent {
  nodeIds: string[];
  delta: WorldPoint;
  worldPoint: WorldPoint;
}

/**
 * DirectCanvas provides an infinite canvas with pan/zoom and node rendering.
 *
 * Features:
 * - HTML layer with positioned div nodes
 * - Camera with pan and zoom
 * - Coordinate transforms (screen ↔ world)
 * - Frustum culling via R-tree spatial index
 * - Selection (single, multi, rectangle)
 * - Node dragging
 */
export class DirectCanvas {
  private container: HTMLElement;
  private options: ResolvedCanvasOptions;
  private camera: Camera = { x: 0, y: 0, zoom: 1 };
  private viewport: ViewportSize = { width: 0, height: 0 };

  // Layers
  private webglLayer: WebGLLayer | null = null;
  private gridElement: HTMLElement | null = null; // CSS fallback grid
  private svgLayer: SVGLayer;
  private svgContainer: HTMLElement;
  private htmlLayer: HTMLLayer;
  private htmlContainer: HTMLElement;
  private selectionRectElement: HTMLElement | null = null;

  // WebGL state
  private useWebGLGrid: boolean = false;

  // Content measurement
  private contentMeasurer: ContentMeasurer;

  // Debug overlay
  private debugOverlay: DebugOverlay;
  private debugStylesInjected: boolean = false;

  // Current state
  private nodes: UINode[] = [];
  private nodeMap: Map<string, UINode> = new Map();
  private edges: UIEdge[] = [];

  // Spatial index for efficient queries
  private spatialIndex: SpatialIndex;

  // Selection state
  private selectedNodeIds: Set<string> = new Set();
  private prevSelectedNodeIds: Set<string> = new Set();

  // Render state
  private needsRender: boolean = false;
  private rafId: number | null = null;

  // Cached arrays for render optimization (avoid allocations per frame)
  private cachedVisibleNodes: UINode[] = [];
  private cachedSortedNodes: UINode[] = [];
  private cachedVisibleEdges: UIEdge[] = [];
  private lastVisibleNodeIds: Set<string> = new Set();
  private nodeOrderDirty: boolean = true;

  // Dirty tracking for nodes/edges
  private nodesModified: boolean = true; // Start true to force initial render
  private edgesModified: boolean = true;

  // Debug metrics throttling
  private lastDebugUpdateTime: number = 0;
  private readonly DEBUG_UPDATE_INTERVAL_MS = 100; // Update debug panel at 10fps max

  // Event handlers
  private eventHandlers: Map<CanvasEventType, Set<CanvasEventHandler>> =
    new Map();

  // Resize observer for automatic viewport updates
  private resizeObserver: ResizeObserver | null = null;

  constructor(container: HTMLElement, options?: CanvasOptions) {
    this.container = container;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.spatialIndex = new SpatialIndex();

    // Register built-in renderers (only once)
    if (!renderersRegistered()) {
      registerBuiltinRenderers();
    }

    // Setup container
    this.setupContainer();

    // Create layers (order matters: webgl-grid -> css-grid -> svg -> webgl-textures -> html -> selection rect)
    // Try WebGL first for grid rendering
    this.createWebGLLayer();
    // CSS grid as fallback (only if WebGL unavailable)
    this.createCSSGridLayer();
    this.svgContainer = this.createLayerContainer("svg");
    this.svgLayer = new SVGLayer(this.svgContainer);
    // Initialize texture canvas AFTER SVG so textures render above edges (Phase 0.7)
    if (this.webglLayer) {
      this.webglLayer.initTextureCanvas(this.svgContainer);
    }
    this.htmlContainer = this.createLayerContainer("html");
    this.htmlLayer = new HTMLLayer(this.htmlContainer);
    // Wire up WebGL layer to HTML layer for texture rendering (Phase 0.7)
    this.htmlLayer.setWebGLLayer(this.webglLayer);
    this.createSelectionRectLayer();

    // Content measurement (for auto-sizing nodes)
    this.contentMeasurer = new ContentMeasurer(this.htmlContainer);

    // Initialize debug overlay
    this.injectDebugStyles();
    this.debugOverlay = new DebugOverlay(this.container);
    this.debugOverlay.initialize(
      this.gridElement,
      this.svgContainer,
      this.htmlContainer,
      this.webglLayer?.getCanvas() ?? null,
      this.webglLayer?.getTextureCanvas() ?? null,
    );

    // Subscribe to debug overlay option changes for canvas settings
    this.debugOverlay.setOnOptionChange((option, value) => {
      if (option === "simplifyLOD" && typeof value === "boolean") {
        this.setSimplifyLOD(value);
      }
      // Handle texture generation options
      if (
        option === "textureResolutionMultiplier" &&
        typeof value === "number"
      ) {
        this.setTextureResolutionMultiplier(value, true); // true = invalidate existing textures
      }
      if (option === "textureDefaultTargetSize" && typeof value === "number") {
        this.setDefaultTargetSize(value, true); // true = invalidate existing textures
      }
      // Handle LOD/texture forcing options - trigger re-render
      if (option === "forceLod" || option === "forceTextureRendering") {
        this.invalidate();
      }
    });

    // Wire up texture preview callback for debug panel (Phase 0.7)
    this.debugOverlay.setGetTexturePreviews(() => this.getTexturePreviews());

    // Sync debug overlay with initial canvas options
    this.debugOverlay.setOptions({ simplifyLOD: this.options.simplifyLOD });

    // Initial sizing
    this.resize();

    // Watch for container resize to keep viewport in sync
    this.resizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    this.resizeObserver.observe(this.container);

    // Start render loop
    this.startRenderLoop();
  }

  // ===========================================================================
  // Setup
  // ===========================================================================

  private setupContainer(): void {
    this.container.style.position = "relative";
    this.container.style.overflow = "hidden";
    this.container.classList.add("direct-canvas");

    // The browser can scroll overflow:hidden containers when
    // contentEditable state changes on a focused descendant.
    // This shifts the visible canvas position.  Prevent it by
    // immediately resetting any scroll on the container.
    this.container.addEventListener("scroll", () => {
      this.container.scrollLeft = 0;
      this.container.scrollTop = 0;
    });
  }

  private createLayerContainer(name: string): HTMLElement {
    const layer = document.createElement("div");
    layer.classList.add(`canvas-layer-${name}`);
    layer.style.position = "absolute";
    layer.style.top = "0";
    layer.style.left = "0";
    layer.style.width = "100%";
    layer.style.height = "100%";
    layer.style.pointerEvents = name === "html" ? "auto" : "none";
    // Prevent browser-initiated scroll on layer containers
    layer.addEventListener("scroll", () => {
      layer.scrollLeft = 0;
      layer.scrollTop = 0;
    });
    this.container.appendChild(layer);
    return layer;
  }

  private createWebGLLayer(): void {
    if (!this.options.showGrid) return;

    // Check if WebGL2 is available
    if (!isWebGL2Available()) {
      console.info("WebGL2 not available, using CSS grid fallback");
      return;
    }

    try {
      this.webglLayer = new WebGLLayer(this.container, {
        gridSize: this.options.gridSize,
        gridStyle: "dots",
        onTexturesReady: () => this.invalidate(),
      });

      if (this.webglLayer.available) {
        this.useWebGLGrid = true;
        console.info("WebGL grid initialized");
      } else {
        // WebGL init failed, clean up
        this.webglLayer.destroy();
        this.webglLayer = null;
        console.info("WebGL init failed, using CSS grid fallback");
      }
    } catch (e) {
      console.warn("WebGL layer creation failed:", e);
      this.webglLayer = null;
    }
  }

  private createCSSGridLayer(): void {
    // Only create CSS grid if WebGL is not being used
    if (!this.options.showGrid || this.useWebGLGrid) return;

    this.gridElement = document.createElement("div");
    this.gridElement.classList.add("canvas-grid");
    this.gridElement.style.position = "absolute";
    this.gridElement.style.top = "0";
    this.gridElement.style.left = "0";
    this.gridElement.style.width = "100%";
    this.gridElement.style.height = "100%";
    this.gridElement.style.pointerEvents = "none";
    this.container.appendChild(this.gridElement);

    this.updateCSSGrid();
  }

  private createSelectionRectLayer(): void {
    this.selectionRectElement = document.createElement("div");
    this.selectionRectElement.classList.add("canvas-selection-rect");
    this.selectionRectElement.style.position = "absolute";
    this.selectionRectElement.style.pointerEvents = "none";
    this.selectionRectElement.style.display = "none";
    this.selectionRectElement.style.border =
      "1px solid var(--canvas-selection-border, #3b82f6)";
    this.selectionRectElement.style.backgroundColor =
      "var(--canvas-selection-bg, rgba(59, 130, 246, 0.1))";
    this.container.appendChild(this.selectionRectElement);
  }

  private updateCSSGrid(): void {
    if (!this.gridElement) return;

    const gridSize = this.options.gridSize * this.camera.zoom;

    // Calculate grid offset based on camera position
    const offsetX =
      (-this.camera.x * this.camera.zoom + this.viewport.width / 2) % gridSize;
    const offsetY =
      (-this.camera.y * this.camera.zoom + this.viewport.height / 2) % gridSize;

    // CSS grid background (fallback when WebGL unavailable)
    this.gridElement.style.backgroundImage = `
			radial-gradient(circle, var(--canvas-grid-dot, #ddd) 1px, transparent 1px)
		`;
    this.gridElement.style.backgroundSize = `${gridSize}px ${gridSize}px`;
    this.gridElement.style.backgroundPosition = `${offsetX}px ${offsetY}px`;
  }

  private renderGrid(): void {
    if (this.useWebGLGrid && this.webglLayer) {
      // WebGL grid rendering
      this.webglLayer.setCamera(this.camera);
      this.webglLayer.renderGrid();
    } else {
      // CSS grid fallback
      this.updateCSSGrid();
    }
  }

  private injectDebugStyles(): void {
    if (this.debugStylesInjected) return;

    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-debug-overlay", "");
    styleEl.textContent = DEBUG_OVERLAY_STYLES;
    document.head.appendChild(styleEl);

    this.debugStylesInjected = true;
  }

  // ===========================================================================
  // Event System
  // ===========================================================================

  /**
   * Register an event handler.
   */
  on<T>(event: CanvasEventType, handler: CanvasEventHandler<T>): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler as CanvasEventHandler);
  }

  /**
   * Remove an event handler.
   */
  off<T>(event: CanvasEventType, handler: CanvasEventHandler<T>): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler as CanvasEventHandler);
    }
  }

  /**
   * Emit an event to all registered handlers.
   */
  private emit<T>(event: CanvasEventType, data: T): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        handler(data);
      }
    }
  }

  // ===========================================================================
  // Camera Control
  // ===========================================================================

  /**
   * Get current camera state.
   */
  getCamera(): Camera {
    return { ...this.camera };
  }

  /**
   * Set camera state.
   */
  setCamera(camera: Camera): void {
    this.camera = {
      x: camera.x,
      y: camera.y,
      zoom: geometry.clampZoom(
        camera.zoom,
        this.options.minZoom,
        this.options.maxZoom,
      ),
    };

    this.svgLayer.setCamera(this.camera);
    this.htmlLayer.setCamera(this.camera);
    this.invalidate();
  }

  /**
   * Pan camera to center on a world point.
   */
  panTo(worldX: number, worldY: number, _animate?: boolean): void {
    // TODO: Animation support in later phase
    this.setCamera({ ...this.camera, x: worldX, y: worldY });
  }

  /**
   * Zoom to a specific level, optionally focusing on a world point.
   */
  zoomTo(zoom: number, focusWorldX?: number, focusWorldY?: number): void {
    const clampedZoom = geometry.clampZoom(
      zoom,
      this.options.minZoom,
      this.options.maxZoom,
    );

    if (focusWorldX !== undefined && focusWorldY !== undefined) {
      // Zoom toward the focus point
      const screenPoint = this.worldToScreen(focusWorldX, focusWorldY);
      const newCamera = geometry.zoomTowardPoint(
        this.camera,
        clampedZoom,
        screenPoint,
        this.viewport,
      );
      this.setCamera(newCamera);
    } else {
      // Zoom toward center
      this.setCamera({ ...this.camera, zoom: clampedZoom });
    }
  }

  /**
   * Fit camera to show all nodes (or specific nodes if IDs provided).
   */
  fitToNodes(
    nodeIds?: string[],
    padding: number = 50,
    maxFill: number = 1.0,
    minZoom?: number,
    maxZoom?: number,
  ): void {
    const targetNodes = nodeIds
      ? this.nodes.filter((n) => nodeIds.includes(n.id))
      : this.nodes;

    const newCamera = geometry.fitToNodes(
      targetNodes,
      this.viewport,
      padding,
      minZoom ?? this.options.minZoom ?? 0.1,
      maxZoom ?? this.options.maxZoom ?? 5,
      maxFill,
    );

    this.setCamera(newCamera);
  }

  /**
   * Pan camera by screen delta (for drag interactions).
   */
  pan(screenDeltaX: number, screenDeltaY: number): void {
    const newCamera = geometry.panByScreenDelta(
      this.camera,
      screenDeltaX,
      screenDeltaY,
    );
    this.setCamera(newCamera);
  }

  /**
   * Zoom by factor toward a screen point.
   */
  zoom(factor: number, screenX: number, screenY: number): void {
    const newZoom = geometry.clampZoom(
      this.camera.zoom * factor,
      this.options.minZoom,
      this.options.maxZoom,
    );

    const newCamera = geometry.zoomTowardPoint(
      this.camera,
      newZoom,
      { x: screenX, y: screenY },
      this.viewport,
    );

    this.setCamera(newCamera);
  }

  // ===========================================================================
  // Coordinate Conversion
  // ===========================================================================

  /**
   * Convert screen coordinates to world coordinates.
   */
  screenToWorld(screenX: number, screenY: number): WorldPoint {
    return geometry.screenToWorld(
      { x: screenX, y: screenY },
      this.camera,
      this.viewport,
    );
  }

  /**
   * Convert world coordinates to screen coordinates.
   */
  worldToScreen(worldX: number, worldY: number): ScreenPoint {
    return geometry.worldToScreen(
      { x: worldX, y: worldY },
      this.camera,
      this.viewport,
    );
  }

  // ===========================================================================
  // Viewport
  // ===========================================================================

  /**
   * Get viewport dimensions.
   */
  getViewport(): ViewportSize {
    return { ...this.viewport };
  }

  /**
   * Get viewport width.
   */
  getViewportWidth(): number {
    return this.viewport.width;
  }

  /**
   * Get viewport height.
   */
  getViewportHeight(): number {
    return this.viewport.height;
  }

  /**
   * Update viewport size from container.
   * Call this when the container size changes.
   */
  resize(): void {
    const rect = this.container.getBoundingClientRect();
    this.viewport = { width: rect.width, height: rect.height };

    // Update all layers
    if (this.webglLayer) {
      this.webglLayer.setViewportSize(rect.width, rect.height);
    }
    this.svgLayer.setViewportSize(rect.width, rect.height);
    this.htmlLayer.setViewportSize(rect.width, rect.height);

    this.invalidate();
  }

  // ===========================================================================
  // Rendering
  // ===========================================================================

  /**
   * Update nodes and edges to render.
   */
  render(nodes: UINode[], edges: UIEdge[]): void {
    this.nodes = nodes;
    this.edges = edges;

    // Update node map for quick lookups
    this.nodeMap.clear();
    for (const node of nodes) {
      this.nodeMap.set(node.id, node);
    }

    // Rebuild spatial index
    this.spatialIndex.rebuild(nodes);

    // Clean up selection (remove any selected IDs that no longer exist)
    const validSelection = new Set<string>();
    for (const id of this.selectedNodeIds) {
      if (this.nodeMap.has(id)) {
        validSelection.add(id);
      }
    }
    if (validSelection.size !== this.selectedNodeIds.size) {
      this.selectedNodeIds = validSelection;
    }

    // Mark as modified to force layer updates
    this.nodesModified = true;
    this.edgesModified = true;
    this.nodeOrderDirty = true;

    this.invalidate();
  }

  /**
   * Mark canvas as needing re-render.
   */
  invalidate(): void {
    this.needsRender = true;
  }

  /**
   * Invalidate a specific node's cached render state and mark canvas for re-render.
   * Forces the node's content to be re-rendered even if its data hasn't changed.
   */
  invalidateNode(nodeId: string): void {
    this.htmlLayer.invalidateNode(nodeId);
    this.needsRender = true;
  }

  private startRenderLoop(): void {
    const loop = () => {
      // Track frame for FPS calculation
      this.debugOverlay.trackFrame();

      // Keep rendering if grid is animated
      const isAnimated = this.webglLayer?.isAnimatedGrid();

      if (this.needsRender || isAnimated) {
        this.doRender();
        this.needsRender = false;
      }

      // Always render debug overlay (for FPS updates)
      this.debugOverlay.render();

      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private doRender(): void {
    // Render grid (WebGL or CSS fallback)
    this.renderGrid();

    // Use spatial index for frustum culling
    const frustum = geometry.getVisibleWorldRect(this.camera, this.viewport);
    // Expand frustum slightly for smoother culling
    const margin = 100 / this.camera.zoom;
    const expandedFrustum: WorldRect = {
      x: frustum.x - margin,
      y: frustum.y - margin,
      width: frustum.width + margin * 2,
      height: frustum.height + margin * 2,
    };

    const visibleNodeIds = this.spatialIndex.queryRect(expandedFrustum);

    // Check if visible nodes changed (for cache invalidation)
    const visibleNodesChanged = this.hasVisibleNodesChanged(visibleNodeIds);

    // Check if selection changed
    const selectionChanged = this.hasSelectionChanged();
    if (selectionChanged) {
      this.prevSelectedNodeIds = new Set(this.selectedNodeIds);
    }

    // Rebuild visible nodes array if visibility changed OR if nodes were modified
    // (nodesModified means render() was called with new node data - positions may have changed)
    if (visibleNodesChanged || this.nodesModified) {
      // Reuse array - clear and refill
      this.cachedVisibleNodes.length = 0;
      for (const id of visibleNodeIds) {
        const node = this.nodeMap.get(id);
        if (node) {
          this.cachedVisibleNodes.push(node);
        }
      }
      this.nodeOrderDirty = true;
      this.lastVisibleNodeIds = new Set(visibleNodeIds);
    }

    // Apply selection state directly to nodes (mutate in place, don't create new objects)
    if (selectionChanged || visibleNodesChanged || this.nodesModified) {
      for (const node of this.cachedVisibleNodes) {
        node.selected = this.selectedNodeIds.has(node.id);
      }
    }

    // Only re-sort if needed (visibility changed or order might have changed)
    if (this.nodeOrderDirty) {
      // Reuse array - copy and sort
      this.cachedSortedNodes.length = 0;
      for (const node of this.cachedVisibleNodes) {
        this.cachedSortedNodes.push(node);
      }
      this.cachedSortedNodes.sort((a, b) => a.z - b.z);
      this.nodeOrderDirty = false;
    }

    // Render edges first (underneath nodes)
    // Rebuild edge list if visibility changed OR edges were modified
    // (edgesModified means render() was called with new edge data - selection may have changed)
    if (visibleNodesChanged || this.edgesModified) {
      const visibleNodeIdSet = this.lastVisibleNodeIds;
      this.cachedVisibleEdges.length = 0;
      for (const edge of this.edges) {
        if (
          visibleNodeIdSet.has(edge.source) ||
          visibleNodeIdSet.has(edge.target)
        ) {
          this.cachedVisibleEdges.push(edge);
        }
      }
    }

    // Check if we can skip edge rendering (edges only change when nodes move or visibility changes)
    const needsEdgeUpdate =
      visibleNodesChanged || this.nodesModified || this.edgesModified;

    // Only render edges if something changed that affects them
    if (needsEdgeUpdate) {
      this.svgLayer.renderEdges(this.cachedVisibleEdges, this.nodeMap);
      this.edgesModified = false;
    }

    // Pass render options to HTML layer
    // The HTMLLayer internally tracks if options actually changed
    const debugOptions = this.debugOverlay.getOptions();
    this.htmlLayer.setRenderOptions({
      simplifyLOD: this.options.simplifyLOD,
      forceTextureAtDistance: debugOptions.forceTextureAtDistance,
      textureThreshold: debugOptions.textureThreshold,
      lodFullThreshold: debugOptions.lodFullThreshold,
      lodTitleThreshold: debugOptions.lodTitleThreshold,
      lodIconThreshold: debugOptions.lodIconThreshold,
      enableLodFull: debugOptions.enableLodFull,
      enableLodTitle: debugOptions.enableLodTitle,
      enableLodIcon: debugOptions.enableLodIcon,
      enableLodDot: debugOptions.enableLodDot,
      forceLod: debugOptions.forceLod ?? undefined,
      forceTextureRendering: debugOptions.forceTextureRendering,
    });

    // Always render nodes - HTMLLayer will efficiently skip unchanged nodes
    // This is necessary because nodes in texture-rendering mode need ongoing
    // renderer calls to establish their textures
    this.htmlLayer.renderNodes(this.cachedSortedNodes);
    this.nodesModified = false;

    // Render any batched textured quads (Phase 0.7)
    if (this.webglLayer) {
      this.webglLayer.renderTexturedQuads();
    }

    // Update debug overlay (throttled to avoid expensive DOM queries every frame)
    this.updateDebugOverlay();
  }

  /**
   * Check if visible node IDs have changed since last frame.
   */
  private hasVisibleNodesChanged(newVisibleIds: string[]): boolean {
    if (newVisibleIds.length !== this.lastVisibleNodeIds.size) {
      return true;
    }
    for (const id of newVisibleIds) {
      if (!this.lastVisibleNodeIds.has(id)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if selection has changed since last frame.
   */
  private hasSelectionChanged(): boolean {
    if (this.selectedNodeIds.size !== this.prevSelectedNodeIds.size) {
      return true;
    }
    for (const id of this.selectedNodeIds) {
      if (!this.prevSelectedNodeIds.has(id)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Update debug overlay with throttling to avoid expensive operations every frame.
   */
  private updateDebugOverlay(): void {
    const now = performance.now();
    const debugVisible = this.debugOverlay.isVisible();

    // Always update camera and visible nodes (lightweight)
    this.debugOverlay.setCamera(this.camera, this.viewport);

    // Throttle expensive debug operations
    if (
      !debugVisible ||
      now - this.lastDebugUpdateTime < this.DEBUG_UPDATE_INTERVAL_MS
    ) {
      // Still render the overlay itself for FPS display
      return;
    }

    this.lastDebugUpdateTime = now;

    // Now do the expensive debug updates
    this.debugOverlay.setVisibleNodes(this.cachedVisibleNodes);
    this.debugOverlay.setSpatialIndexBounds(this.spatialIndex.getAllBounds());

    // Collect texture cache stats (Phase 0.7)
    const textureCacheStats = this.webglLayer?.getTextureCacheStats();
    const textureDebugState = this.webglLayer?.getTextureDebugState();

    // Count HTML vs texture-cached nodes (Phase 0.7) - expensive DOM query
    const allNodeElements = this.container.querySelectorAll(".canvas-node");
    const textureNodeElements = this.container.querySelectorAll(
      ".canvas-node.texture-cached",
    );
    const htmlNodesRendered =
      allNodeElements.length - textureNodeElements.length;
    const textureNodesRendered = textureNodeElements.length;

    // Apply texture debug classes to nodes (Phase 0.7)
    if (this.debugOverlay.getOptions().showTextureQueue && textureDebugState) {
      this.applyTextureDebugClasses(textureDebugState);
    } else {
      this.clearTextureDebugClasses();
    }

    // Aggregate skip reasons for metrics
    const recentSkipReasons: Record<string, number> = {};
    if (textureDebugState?.recentSkips) {
      for (const skip of textureDebugState.recentSkips) {
        recentSkipReasons[skip.reason] =
          (recentSkipReasons[skip.reason] || 0) + 1;
      }
    }

    this.debugOverlay.updateMetrics({
      totalNodes: this.nodes.length,
      visibleNodes: this.cachedVisibleNodes.length,
      selectedNodes: this.selectedNodeIds.size,
      totalEdges: this.edges.length,
      domElements: allNodeElements.length,
      spatialIndexSize: this.spatialIndex.size,
      // Phase 0.7: Texture cache stats
      textureCacheCount: textureCacheStats?.count ?? 0,
      textureCacheMemoryMB:
        (textureCacheStats?.memoryBytes ?? 0) / (1024 * 1024),
      textureCacheHits: textureCacheStats?.hits ?? 0,
      textureCacheMisses: textureCacheStats?.misses ?? 0,
      texturePendingRequests: textureCacheStats?.pendingRequests ?? 0,
      // Phase 0.7: Entity rendering counts
      htmlNodesRendered,
      textureNodesRendered,
      svgEdgesRendered: this.cachedVisibleEdges.length,
      // Phase 0.7: Texture queue debug stats
      textureQueueLength: textureDebugState?.queueLength ?? 0,
      texturesGenerated: textureDebugState?.texturesGenerated ?? 0,
      texturesSkipped: textureDebugState?.texturesSkipped ?? 0,
      currentlyProcessing: textureDebugState?.currentlyProcessing ?? null,
      recentSkipReasons,
    });
  }

  /**
   * Apply texture debug CSS classes to nodes based on current queue state.
   */
  private applyTextureDebugClasses(debugState: {
    pendingIds: string[];
    cachedIds: string[];
    currentlyProcessing: string | null;
    recentSkips: Array<{ nodeId: string; reason: string; timestamp: number }>;
  }): void {
    const allNodes = this.container.querySelectorAll(".canvas-node");

    // Create sets for quick lookup
    const pendingSet = new Set(debugState.pendingIds);
    const skippedSet = new Set(debugState.recentSkips.map((s) => s.nodeId));

    allNodes.forEach((nodeEl) => {
      const element = nodeEl as HTMLElement;
      const nodeId = element.dataset.nodeId;
      if (!nodeId) return;

      // Get current LOD to form compound ID
      const lod = element.dataset.lod || "full";
      const compoundId = `${nodeId}:${lod}`;

      // Check states (compound IDs are used in texture cache)
      const isPending = pendingSet.has(compoundId);
      const isProcessing = debugState.currentlyProcessing === compoundId;
      const wasSkipped = skippedSet.has(compoundId);

      // Apply classes
      element.classList.toggle("texture-pending", isPending && !isProcessing);
      element.classList.toggle("texture-processing", isProcessing);
      element.classList.toggle(
        "texture-skipped",
        wasSkipped && !isPending && !isProcessing,
      );
    });
  }

  /**
   * Clear texture debug CSS classes from all nodes.
   */
  private clearTextureDebugClasses(): void {
    const debugClasses = [
      "texture-pending",
      "texture-processing",
      "texture-skipped",
    ];
    debugClasses.forEach((cls) => {
      this.container.querySelectorAll(`.canvas-node.${cls}`).forEach((el) => {
        el.classList.remove(cls);
      });
    });
  }

  /**
   * Get nodes that are visible in the current viewport.
   */
  private getVisibleNodes(): UINode[] {
    const frustum = geometry.getVisibleWorldRect(this.camera, this.viewport);
    const margin = 100 / this.camera.zoom;
    const expandedFrustum: WorldRect = {
      x: frustum.x - margin,
      y: frustum.y - margin,
      width: frustum.width + margin * 2,
      height: frustum.height + margin * 2,
    };

    const visibleNodeIds = this.spatialIndex.queryRect(expandedFrustum);
    return visibleNodeIds
      .map((id) => this.nodeMap.get(id))
      .filter((node): node is UINode => node !== undefined);
  }

  // ===========================================================================
  // Selection
  // ===========================================================================

  /**
   * Get selected node IDs.
   */
  getSelectedNodeIds(): string[] {
    return Array.from(this.selectedNodeIds);
  }

  /**
   * Check if a node is selected.
   */
  isNodeSelected(nodeId: string): boolean {
    return this.selectedNodeIds.has(nodeId);
  }

  /**
   * Select nodes, optionally adding to or removing from current selection.
   */
  selectNodes(
    nodeIds: string[],
    options?: { add?: boolean; toggle?: boolean },
  ): void {
    const previousSelection = new Set(this.selectedNodeIds);
    const add = options?.add ?? false;
    const toggle = options?.toggle ?? false;

    if (!add && !toggle) {
      // Replace selection
      this.selectedNodeIds.clear();
    }

    for (const id of nodeIds) {
      if (!this.nodeMap.has(id)) continue;

      if (toggle && this.selectedNodeIds.has(id)) {
        this.selectedNodeIds.delete(id);
      } else {
        this.selectedNodeIds.add(id);
      }
    }

    // Emit event if selection changed
    const added: string[] = [];
    const removed: string[] = [];

    for (const id of this.selectedNodeIds) {
      if (!previousSelection.has(id)) added.push(id);
    }
    for (const id of previousSelection) {
      if (!this.selectedNodeIds.has(id)) removed.push(id);
    }

    if (added.length > 0 || removed.length > 0) {
      this.emit<SelectionChangeEvent>("selectionchange", {
        selected: this.getSelectedNodeIds(),
        added,
        removed,
      });
      this.invalidate();
    }
  }

  /**
   * Deselect nodes.
   */
  deselectNodes(nodeIds: string[]): void {
    const previousSelection = new Set(this.selectedNodeIds);
    const removed: string[] = [];

    for (const id of nodeIds) {
      if (this.selectedNodeIds.delete(id)) {
        removed.push(id);
      }
    }

    if (removed.length > 0) {
      this.emit<SelectionChangeEvent>("selectionchange", {
        selected: this.getSelectedNodeIds(),
        added: [],
        removed,
      });
      this.invalidate();
    }
  }

  /**
   * Clear all selection.
   */
  clearSelection(): void {
    if (this.selectedNodeIds.size === 0) return;

    const removed = Array.from(this.selectedNodeIds);
    this.selectedNodeIds.clear();

    this.emit<SelectionChangeEvent>("selectionchange", {
      selected: [],
      added: [],
      removed,
    });
    this.invalidate();
  }

  /**
   * Select all nodes.
   */
  selectAll(): void {
    const previousSelection = new Set(this.selectedNodeIds);
    const added: string[] = [];

    for (const node of this.nodes) {
      if (!this.selectedNodeIds.has(node.id)) {
        this.selectedNodeIds.add(node.id);
        added.push(node.id);
      }
    }

    if (added.length > 0) {
      this.emit<SelectionChangeEvent>("selectionchange", {
        selected: this.getSelectedNodeIds(),
        added,
        removed: [],
      });
      this.invalidate();
    }
  }

  // ===========================================================================
  // Selection Rectangle
  // ===========================================================================

  /**
   * Show selection rectangle (for marquee selection).
   */
  showSelectionRect(screenRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): void {
    if (!this.selectionRectElement) return;

    this.selectionRectElement.style.display = "block";
    this.selectionRectElement.style.left = `${screenRect.x}px`;
    this.selectionRectElement.style.top = `${screenRect.y}px`;
    this.selectionRectElement.style.width = `${screenRect.width}px`;
    this.selectionRectElement.style.height = `${screenRect.height}px`;
  }

  /**
   * Hide selection rectangle.
   */
  hideSelectionRect(): void {
    if (!this.selectionRectElement) return;
    this.selectionRectElement.style.display = "none";
  }

  /**
   * Get nodes within a screen rect (for marquee selection).
   */
  getNodesInScreenRect(screenRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): string[] {
    // Handle negative width/height (dragging from right to left)
    const normalizedRect = {
      x: screenRect.width < 0 ? screenRect.x + screenRect.width : screenRect.x,
      y:
        screenRect.height < 0 ? screenRect.y + screenRect.height : screenRect.y,
      width: Math.abs(screenRect.width),
      height: Math.abs(screenRect.height),
    };

    const worldTopLeft = this.screenToWorld(normalizedRect.x, normalizedRect.y);
    const worldBottomRight = this.screenToWorld(
      normalizedRect.x + normalizedRect.width,
      normalizedRect.y + normalizedRect.height,
    );

    const worldRect: WorldRect = {
      x: worldTopLeft.x,
      y: worldTopLeft.y,
      width: worldBottomRight.x - worldTopLeft.x,
      height: worldBottomRight.y - worldTopLeft.y,
    };

    return this.spatialIndex.queryRect(worldRect);
  }

  // ===========================================================================
  // Node Manipulation
  // ===========================================================================

  /**
   * Move nodes by a world delta.
   * Returns updated nodes (caller should update their state).
   */
  moveNodes(nodeIds: string[], deltaX: number, deltaY: number): UINode[] {
    const movedNodes: UINode[] = [];

    for (const id of nodeIds) {
      const node = this.nodeMap.get(id);
      if (!node) continue;

      node.position = {
        x: node.position.x + deltaX,
        y: node.position.y + deltaY,
      };

      // Update spatial index
      const bounds = geometry.getNodeBounds(node.position, node.size);
      this.spatialIndex.update(id, bounds);

      movedNodes.push(node);
    }

    if (movedNodes.length > 0) {
      this.nodesModified = true;
      this.edgesModified = true; // Edges need update when connected nodes move
      this.invalidate();
    }

    return movedNodes;
  }

  /**
   * Get a node by ID.
   */
  getNode(nodeId: string): UINode | undefined {
    return this.nodeMap.get(nodeId);
  }

  /**
   * Get all nodes.
   */
  getNodes(): UINode[] {
    return [...this.nodes];
  }

  /**
   * Get an edge by ID.
   */
  getEdge(edgeId: string): UIEdge | undefined {
    return this.edges.find((e) => e.id === edgeId);
  }

  /**
   * Get all edges.
   */
  getEdges(): UIEdge[] {
    return [...this.edges];
  }

  // ===========================================================================
  // Hit Testing
  // ===========================================================================

  /**
   * Get node at a world point.
   * Returns the topmost node (highest z) at that point.
   */
  getNodeAtPoint(worldX: number, worldY: number): string | null {
    const point: WorldPoint = { x: worldX, y: worldY };

    // Use spatial index for initial candidates
    const candidates = this.spatialIndex.queryPoint(point);

    if (candidates.length === 0) return null;

    // Find the node with highest z among candidates
    let topNode: UINode | null = null;
    let topZ = -Infinity;

    for (const id of candidates) {
      const node = this.nodeMap.get(id);
      if (!node) continue;

      // Verify the point is actually inside the node bounds
      const bounds = geometry.getNodeBounds(node.position, node.size);
      if (geometry.rectContainsPoint(bounds, point)) {
        if (node.z > topZ) {
          topZ = node.z;
          topNode = node;
        }
      }
    }

    return topNode?.id ?? null;
  }

  /**
   * Get node at a screen point (spatial index based).
   */
  getNodeAtScreenPoint(screenX: number, screenY: number): string | null {
    const world = this.screenToWorld(screenX, screenY);
    return this.getNodeAtPoint(world.x, world.y);
  }

  /**
   * Get node at a screen point using DOM hit testing.
   * More accurate than spatial index for complex node shapes.
   */
  getNodeAtScreenPointDOM(screenX: number, screenY: number): string | null {
    return this.htmlLayer.getNodeAtPoint(screenX, screenY);
  }

  /**
   * Get nodes within a world rect.
   */
  getNodesInRect(rect: WorldRect): string[] {
    return this.spatialIndex.queryRect(rect);
  }

  /**
   * Get visible world rect (frustum).
   */
  getVisibleWorldRect(): WorldRect {
    return geometry.getVisibleWorldRect(this.camera, this.viewport);
  }

  // ===========================================================================
  // DOM Access
  // ===========================================================================

  /**
   * Get the container element.
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * Get the DOM element for a node.
   */
  getNodeElement(nodeId: string): HTMLElement | null {
    return this.htmlLayer.getNodeElement(nodeId);
  }

  // ===========================================================================
  // Content Measurement
  // ===========================================================================

  /**
   * Measure the intrinsic content size for a node.
   * Returns null if the renderer doesn't support measurement.
   */
  measureNodeContent(node: UINode, extraOptions?: MeasureOptions): ContentMeasurement | null {
    const renderer = rendererRegistry.get(node.type);
    if (!renderer?.getContentHTML) return null;
    const html = renderer.getContentHTML(node);
    const options = { ...(renderer.getMeasureOptions?.(node) ?? {}), ...extraOptions };
    return this.contentMeasurer.measure(html, node.type, options);
  }

  /**
   * Measure the intrinsic content size for a node, waiting for images to load.
   * Returns null if the renderer doesn't support measurement.
   */
  async measureNodeContentAsync(
    node: UINode,
    extraOptions?: MeasureOptions,
  ): Promise<ContentMeasurement | null> {
    const renderer = rendererRegistry.get(node.type);
    if (!renderer?.getContentHTML) return null;
    const html = renderer.getContentHTML(node);
    const options = { ...(renderer.getMeasureOptions?.(node) ?? {}), ...extraOptions };
    return this.contentMeasurer.measureAsync(html, node.type, options);
  }

  // ===========================================================================
  // Debug
  // ===========================================================================

  /**
   * Get current debug options.
   */
  getDebugOptions(): DebugOptions {
    return this.debugOverlay.getOptions();
  }

  /**
   * Set debug options.
   */
  setDebugOptions(options: Partial<DebugOptions>): void {
    this.debugOverlay.setOptions(options);
  }

  /**
   * Toggle debug panel visibility.
   */
  toggleDebug(): void {
    this.debugOverlay.toggle();
  }

  // ===========================================================================
  // WebGL
  // ===========================================================================

  /**
   * Check if WebGL is being used for grid rendering.
   */
  isUsingWebGL(): boolean {
    return this.useWebGLGrid;
  }

  /**
   * Check if WebGL is available (even if not currently used).
   */
  isWebGLAvailable(): boolean {
    return this.webglLayer?.available ?? false;
  }

  /**
   * Set grid style (dots, lines, or none).
   */
  setGridStyle(style: GridStyle): void {
    if (this.webglLayer) {
      this.webglLayer.setGridOptions({ gridStyle: style });
    }
    this.invalidate();
  }

  /**
   * Set WebGL background and grid colors for theme support.
   * Colors are normalized RGBA arrays [0..1].
   */
  setGridColors(
    backgroundColor: [number, number, number, number],
    gridColor: [number, number, number, number],
  ): void {
    if (this.webglLayer) {
      this.webglLayer.setGridOptions({ backgroundColor, gridColor });
    }
    this.invalidate();
  }

  // ===========================================================================
  // Render Options
  // ===========================================================================

  /**
   * Get whether LOD simplification is enabled.
   * When true, nodes simplify their content at smaller zoom levels (title → icon → dot).
   * When false, nodes always render full content regardless of zoom.
   */
  getSimplifyLOD(): boolean {
    return this.options.simplifyLOD;
  }

  /**
   * Set whether LOD simplification is enabled.
   * When true, nodes simplify their content at smaller zoom levels (title → icon → dot).
   * When false, nodes always render full content regardless of zoom.
   */
  setSimplifyLOD(enabled: boolean): void {
    if (this.options.simplifyLOD !== enabled) {
      this.options.simplifyLOD = enabled;
      // Sync debug overlay checkbox (the callback check prevents infinite loops)
      this.debugOverlay.setOptions({ simplifyLOD: enabled });
      this.invalidate();
    }
  }

  // ===========================================================================
  // Phase 0.7 LOD + Textures API
  // ===========================================================================

  /**
   * Get whether LOD (Level of Detail) system is enabled.
   * Phase 0.7 API - currently maps to simplifyLOD.
   */
  getEnableLOD(): boolean {
    return this.options.simplifyLOD;
  }

  /**
   * Set whether LOD (Level of Detail) system is enabled.
   * Phase 0.7 API - currently maps to simplifyLOD.
   */
  setEnableLOD(enabled: boolean): void {
    this.setSimplifyLOD(enabled);
  }

  /**
   * Get whether render-to-texture is enabled.
   * Phase 0.7 API - stub implementation, textures not yet implemented.
   */
  getEnableTextures(): boolean {
    // TODO: Implement in Phase 0.7
    return false;
  }

  /**
   * Set whether render-to-texture is enabled.
   * Phase 0.7 API - stub implementation, textures not yet implemented.
   */
  setEnableTextures(_enabled: boolean): void {
    // TODO: Implement in Phase 0.7
    // For now, this is a no-op
  }

  /**
   * Get texture system statistics.
   * Phase 0.7 API.
   */
  getTextureStats(): {
    cachedCount: number;
    atlasCount: number;
    queueLength: number;
  } {
    const stats = this.webglLayer?.getTextureCacheStats();
    const debugState = this.webglLayer?.getTextureDebugState();
    return {
      cachedCount: stats?.count ?? 0,
      atlasCount: 0, // Atlas system not yet implemented
      queueLength: debugState?.queueLength ?? 0,
    };
  }

  /**
   * Get detailed texture debug state for diagnosing texture system behavior.
   * Phase 0.7 API.
   */
  getTextureDebugState(): {
    pendingIds: string[];
    cachedIds: string[];
    currentlyProcessing: string | null;
    recentSkips: Array<{ nodeId: string; reason: string; timestamp: number }>;
    queueLength: number;
    texturesGenerated: number;
    texturesSkipped: number;
    stats: { count: number; memoryBytes: number; hits: number; misses: number };
  } | null {
    return this.webglLayer?.getTextureDebugState() ?? null;
  }

  /**
   * Get texture preview thumbnails for debug inspection.
   * Phase 0.7 API.
   */
  getTexturePreviews(
    maxCount?: number,
    thumbnailSize?: number,
  ): Array<{
    nodeId: string;
    width: number;
    height: number;
    memoryBytes: number;
    dataUrl: string;
  }> {
    return this.webglLayer?.getTexturePreviews(maxCount, thumbnailSize) ?? [];
  }

  /**
   * Get texture resolution multiplier.
   * Controls the size of generated textures relative to the target size.
   * Default: 1.0 (texture size = target size)
   */
  getTextureResolutionMultiplier(): number {
    return this.webglLayer?.getTextureResolutionMultiplier() ?? 1.0;
  }

  /**
   * Set texture resolution multiplier.
   * @param multiplier - Multiplier for texture target size (0.1 to 4.0)
   * @param invalidateExisting - If true, invalidate all existing textures (default: false)
   */
  setTextureResolutionMultiplier(
    multiplier: number,
    invalidateExisting: boolean = false,
  ): void {
    this.webglLayer?.setTextureResolutionMultiplier(
      multiplier,
      invalidateExisting,
    );
  }

  /**
   * Get default target size for textures.
   * This is the max dimension textures will be resized to.
   * Default: 150 pixels
   */
  getDefaultTargetSize(): number {
    return this.webglLayer?.getDefaultTargetSize() ?? 150;
  }

  /**
   * Set default target size for textures.
   * @param size - Target size in pixels (32 to 1024)
   * @param invalidateExisting - If true, invalidate all existing textures (default: false)
   */
  setDefaultTargetSize(
    size: number,
    invalidateExisting: boolean = false,
  ): void {
    this.webglLayer?.setDefaultTargetSize(size, invalidateExisting);
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /**
   * Clean up resources.
   */
  destroy(): void {
    // Stop resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Stop render loop
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Destroy layers
    if (this.webglLayer) {
      this.webglLayer.destroy();
      this.webglLayer = null;
    }
    this.svgLayer.destroy();
    this.htmlLayer.destroy();

    // Destroy content measurer
    this.contentMeasurer.destroy();

    // Destroy debug overlay
    this.debugOverlay.destroy();

    // Remove CSS grid fallback
    if (this.gridElement) {
      this.gridElement.remove();
      this.gridElement = null;
    }

    // Remove selection rect
    if (this.selectionRectElement) {
      this.selectionRectElement.remove();
      this.selectionRectElement = null;
    }

    // Clear spatial index
    this.spatialIndex.clear();

    // Clear event handlers
    this.eventHandlers.clear();

    // Remove container class
    this.container.classList.remove("direct-canvas");
  }
}
